/**
 * useImportDictionary -- the non-SoR ingest data dictionary (P117.1).
 *
 * GET /v1/admin/foundation/import/dictionary. Admin-only (the endpoint
 * 403s otherwise); callers gate the surrounding UI with useMe().
 *
 * `downloadImportTemplate` fetches a per-entity CSV template as a Blob
 * and triggers a browser download -- it bypasses `api.get` (which
 * JSON-parses bodies) and attaches the bearer token manually, the same
 * posture as the board-PDF / dossier downloads.
 */
import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError, api, currentAuthToken } from "shared/api/client";
import type {
  ImportBatch,
  ImportBatchSummary,
  ImportCompleteness,
  ImportDictionary,
  ImportUndoResult,
} from "shared/api/types";

export const importKeys = {
  dictionary: ["import", "dictionary"] as const,
  completeness: ["import", "completeness"] as const,
  /** Round 13 D8: the importer's own history. */
  batches: (kind: string) => ["import", "batches", kind] as const,
  batch: (id: string) => ["import", "batch", id] as const,
};

export function useImportDictionary(opts?: {
  enabled?: boolean;
}): UseQueryResult<ImportDictionary> {
  return useQuery({
    queryKey: importKeys.dictionary,
    queryFn: () => api.get<ImportDictionary>("/v1/admin/foundation/import/dictionary"),
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useImportCompleteness(opts?: {
  enabled?: boolean;
}): UseQueryResult<ImportCompleteness> {
  return useQuery({
    queryKey: importKeys.completeness,
    queryFn: () =>
      api.get<ImportCompleteness>("/v1/admin/foundation/import/completeness"),
    enabled: opts?.enabled ?? true,
    staleTime: 30_000,
  });
}

export class ImportError extends Error {
  constructor(
    public readonly status: number,
    detail: string,
  ) {
    super(detail);
    this.name = "ImportError";
  }
}

async function _uploadCsv(input: {
  kind: string;
  file: File;
  assisted?: boolean;
  autoCommit?: boolean;
  resolveEnums?: boolean;
}): Promise<ImportBatch> {
  const token = currentAuthToken();
  const form = new FormData();
  form.append("file", input.file);
  // P137: the assisted path routes through the Engine column-mapper.
  const suffix = input.assisted ? "/assisted" : "";
  // P233 auto-commit + P238 resolve_enums apply only on the assisted path.
  const params = new URLSearchParams();
  if (input.assisted && input.autoCommit) params.set("auto_commit", "true");
  if (input.assisted && input.resolveEnums) params.set("resolve_enums", "true");
  const query = params.toString() ? `?${params}` : "";
  const response = await fetch(
    `/v1/admin/foundation/import/${input.kind}${suffix}${query}`,
    {
      method: "POST",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    },
  );
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      const body = await response.json();
      // The assisted 502 detail is an object {message, raw}; others a string.
      const d = body?.detail;
      detail = typeof d === "string" ? d : (d?.message ?? detail);
    } catch {
      // keep statusText
    }
    throw new ImportError(response.status, detail);
  }
  return (await response.json()) as ImportBatch;
}

export function useUploadCsv(): UseMutationResult<
  ImportBatch,
  ImportError,
  {
    kind: string;
    file: File;
    assisted?: boolean;
    autoCommit?: boolean;
    resolveEnums?: boolean;
  }
> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: _uploadCsv,
  });
}

export function useCommitBatch(): UseMutationResult<ImportBatch, Error, string> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (batchId: string) =>
      api.post<ImportBatch>(`/v1/admin/foundation/import/batches/${batchId}/commit`),
  });
}

/** Round 13 D8 -- every upload this estate has seen, newest first.
 *
 * Fifteen staged batches sat on one estate invisible to every screen,
 * so an operator who uploaded a file, left, and came back had no way to
 * find the review he had not finished. */
export function useImportBatches(
  kind: string,
  opts?: { enabled?: boolean },
): UseQueryResult<ImportBatchSummary[]> {
  return useQuery({
    queryKey: importKeys.batches(kind),
    queryFn: () =>
      api.get<ImportBatchSummary[]>(
        `/v1/admin/foundation/import/batches${
          kind ? `?entity_kind=${encodeURIComponent(kind)}&include_staged=true` : ""
        }`,
      ),
    enabled: opts?.enabled ?? true,
    staleTime: 15_000,
  });
}

/** One batch by id -- the target of `?batch=<id>`, which the screen
 * ignored for the whole of round 13's walk. */
export function useImportBatch(
  batchId: string,
  opts?: { enabled?: boolean },
): UseQueryResult<ImportBatch> {
  return useQuery({
    queryKey: importKeys.batch(batchId),
    queryFn: () =>
      api.get<ImportBatch>(`/v1/admin/foundation/import/batches/${batchId}`),
    enabled: (opts?.enabled ?? true) && Boolean(batchId),
  });
}

/** Throw away a STAGED batch. Nothing live was ever written.
 *
 * Named `...ImportBatch` rather than `useDiscardBatch`: the document
 * locker already exports a hook by that name, and the handled-failure
 * ratchet matches on the bare name across the tree -- so two hooks
 * sharing one would let a marker on this side vouch for the other. */
export function useDiscardImportBatch(): UseMutationResult<void, Error, string> {
  return useMutation({
    meta: { handled: true },
    mutationFn: (batchId: string) =>
      api.delete(`/v1/admin/foundation/import/batches/${batchId}`),
  });
}

/** Reverse what a committed batch wrote, where reversing is safe.
 *
 * Partial by design: a record the estate has since built on, or one a
 * person has since corrected, comes back in `refused` rather than being
 * forced. */
export function useUndoImportBatch(): UseMutationResult<
  ImportUndoResult,
  Error,
  string
> {
  return useMutation({
    meta: { handled: true },
    /* ROUND 16 E10 -- when the gateway answers instead of the server,
       the batch says what happened: an undo the edge gave up on had
       removed all 5,000 rows while the screen said nothing was
       recorded. Read it again rather than guess. */
    mutationFn: async (batchId: string) => {
      try {
        return await api.post<ImportUndoResult>(
          `/v1/admin/foundation/import/batches/${batchId}/undo`,
        );
      } catch (err) {
        if (
          !(err instanceof ApiError) ||
          (err.code !== "refusal.gateway_timeout" &&
            err.code !== "refusal.gateway_unavailable")
        ) {
          throw err;
        }
        const batch = await api.get<{
          status: string;
          insert_count: number;
          update_count: number;
        }>(`/v1/admin/foundation/import/batches/${batchId}`);
        if (batch.status === "undone") {
          return {
            reverted_inserts: batch.insert_count,
            reverted_updates: batch.update_count,
            refused: [],
            status: batch.status,
          };
        }
        throw new ImportUndoUnsure(err);
      }
    },
  });
}

/** ROUND 16 E10 -- the gateway gave up and the batch, read again, has
 * not been undone yet. Said as that, never as "nothing was recorded". */
export class ImportUndoUnsure extends Error {
  constructor(public readonly cause: ApiError) {
    super("undo outcome not yet known");
    this.name = "ImportUndoUnsure";
  }
}

export class ImportTemplateError extends Error {
  constructor(
    public readonly status: number,
    detail: string,
  ) {
    super(detail);
    this.name = "ImportTemplateError";
  }
}

function _filename(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = header.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? fallback;
}

async function _downloadCsv(path: string, fallback: string): Promise<void> {
  const token = currentAuthToken();
  const response = await fetch(path, {
    method: "GET",
    headers: {
      Accept: "text/csv",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      const body = await response.json();
      detail = body?.detail ?? detail;
    } catch {
      // non-JSON body; keep statusText
    }
    throw new ImportTemplateError(response.status, detail);
  }
  const filename = _filename(response.headers.get("Content-Disposition"), fallback);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export async function downloadImportTemplate(kind: string): Promise<void> {
  await _downloadCsv(
    `/v1/admin/foundation/import/template/${kind}.csv`,
    `marsad-${kind}-template.csv`,
  );
}

/** Round 20 W8 -- every row of a batch: its verdict, target and message. */
export async function downloadBatchOutcome(batchId: string): Promise<void> {
  await _downloadCsv(
    `/v1/admin/foundation/import/batches/${batchId}/rows.csv`,
    `marsad-import-${batchId.slice(0, 8)}-rows.csv`,
  );
}
